import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/components/AuthProvider";

export interface SubscriptionStatus {
  subscribed: boolean;
  subscription_status: 'active' | 'cancelled' | 'past_due' | 'inactive';
  subscription_tier?: 'weekly' | 'monthly' | 'annual' | 'lifetime';
  subscription_source?: 'stripe' | 'apple' | 'google';
  current_period_end?: string;
  has_had_subscription?: boolean; // Tracks if user ever had a subscription (prevents trial abuse)
  has_active_discount?: boolean; // Tracks if user already has a discount applied
  is_lifetime?: boolean;
}

/**
 * Unified subscription status hook that works across all platforms.
 * Reads directly from the user_accounts table which is updated by:
 * - Stripe webhooks (web purchases)
 * - RevenueCat webhooks (iOS/Android IAP)
 * 
 * This ensures cross-platform subscription sync - a user who subscribes
 * on iOS will have full access on web, and vice versa.
 */
export function useSubscriptionStatus() {
  const { session } = useAuth();

  return useQuery({
    queryKey: ['subscriptionStatus', session?.user?.id],
    queryFn: async (): Promise<SubscriptionStatus> => {
      if (!session?.user?.id) {
        return {
          subscribed: false,
          subscription_status: 'inactive'
        };
      }

      // Read directly from user_accounts table (source of truth)
      // Both Stripe and RevenueCat webhooks update this table
      const { data, error } = await supabase
        .from('user_accounts')
        .select('subscription_status, subscription_tier, subscription_source, current_period_end, has_had_subscription, is_admin')
        .eq('user_id', session.user.id)
        .maybeSingle();
      
      if (error) {
        console.error('Error fetching subscription status:', error);
        throw error;
      }

      // If no record exists, user has no subscription
      if (!data) {
        return {
          subscribed: false,
          subscription_status: 'inactive'
        };
      }

      const isAdmin = data.is_admin === true;
      const isActive = data.subscription_status === 'active';
      const isLifetime = data.subscription_tier === 'lifetime';
      
      // For cancelled subscriptions, check if still in grace period
      const inGracePeriod = data.subscription_status === 'cancelled' && 
        data.current_period_end && 
        new Date(data.current_period_end) > new Date();

      return {
        subscribed: isAdmin || isActive || isLifetime || inGracePeriod,
        subscription_status: (data.subscription_status as SubscriptionStatus['subscription_status']) || 'inactive',
        subscription_tier: data.subscription_tier as SubscriptionStatus['subscription_tier'],
        subscription_source: data.subscription_source as SubscriptionStatus['subscription_source'],
        current_period_end: data.current_period_end || undefined,
        has_had_subscription: data.has_had_subscription || false,
        is_lifetime: isLifetime,
      };
    },
    enabled: !!session?.user?.id,
    staleTime: 60000, // Cache for 1 minute
    refetchInterval: 300000, // Refetch every 5 minutes
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    refetchOnWindowFocus: true,
    refetchOnReconnect: true
  });
}

// Alias for backward compatibility
export const useUnifiedSubscription = useSubscriptionStatus;

export function useCreateSubscriptionCheckout() {
  return async (tier: 'weekly' | 'monthly' | 'annual', currency?: string) => {
    const { data, error } = await supabase.functions.invoke('create-subscription-checkout', {
      body: { tier, currency: currency?.toLowerCase() }
    });
    
    if (error) {
      throw error;
    }
    
    return data;
  };
}

export function useCustomerPortal() {
  return async () => {
    const { data, error } = await supabase.functions.invoke('customer-portal');
    
    if (error) {
      throw error;
    }
    
    return data;
  };
}